import { IAttachment } from '@/api/attachment';
import {
  GlobalSchemaFields,
  parseDate,
  populatedArrayCtor,
  populatedCtor,
  populatedListCtor,
  populatedReadCtor,
} from '@/api/mongooseTypes';
import { RESTFUL } from '@/data/constants';
import { AxiosInstance } from 'axios';
import { merge } from 'lodash';

export async function printMailLabel(api, id, language = 'en') {
  return (await api.post(`/rml/admin/clientProfile/printMailLabel/${id}`, { language })).data;
}
/**
 * @param {AxiosInstance} api
 * @param {string} userId
 * @param {*} patch
 */
export async function patchUser(api, userId, patch) {
  return (await api.patch(RESTFUL.user.create + '/' + userId, patch)).data;
}

/**
 * @param {AxiosInstance} api
 * @param {string} profileId
 * @param {*} patch
 */
export async function patchProfile(api, profileId, patch) {
  return (await api.patch(RESTFUL.profile.create + '/' + profileId, patch)).data;
}

/**
 * @param {AxiosInstance} api
 * @param {Partial<IProfile & IUser>} profileAndUserFields
 */
export async function createUser(api, profileAndUserFields) {
  return (await api.post(RESTFUL.user.create, profileAndUserFields)).data;
}

// For RML Staff
/**
 * @param {AxiosInstance} api
 * @param {Partial<IProfile & IUser>} profileAndUserFields
 */
export async function createClientUser(api, profileAndUserFields) {
  return (await api.post('rml/ClientProfile/createUser', profileAndUserFields)).data;
}

/**
 * @param {AxiosInstance} api
 * @param {string} profileId
 */
export async function getUserListByProfileId(api, profileId) {
  return (
    await api.get(RESTFUL.user.list, { params: { filter: { profiles: profileId }, select: '_id' } })
  ).data;
}

/**
 * @param {AxiosInstance} api
 * @param params
 */
export async function listProfiles(api, params) {
  const { data } = await api.get(RESTFUL.profile.list, { params });
  return populatedListCtor(data, IProfile);
}

/**
 * @param {AxiosInstance} api
 * @param {string|string[]} ids
 * @param [params]
 */
export async function readProfile(api, ids, params) {
  if (Array.isArray(ids)) {
    const { data } = await api.get(RESTFUL.profile.read + '/,' + ids.join(','), {
      params,
    });
    return populatedReadCtor(data, IProfile);
  } else {
    const { data } = await api.get(RESTFUL.profile.read + '/' + ids, { params });
    return populatedCtor(data, IProfile);
  }
}

export async function readProfileByMaster(api, ids, params) {
  if (Array.isArray(ids)) {
    const { data } = await api.get(RESTFUL.profile.masterRead + '/,' + ids.join(','), {
      params,
    });
    return populatedReadCtor(data, IProfile);
  } else {
    const { data } = await api.get(RESTFUL.profile.masterRead + '/' + ids, { params });
    return populatedCtor(data, IProfile);
  }
}

/**
 * @param {AxiosInstance} api
 * @param {form} id
 * @param {to} id
 * @return {Promise<ITimetable>}
 */
export async function mergeClient(api, from, to) {
  const data = (
    await api.patch('rml/admin/clientProfile/merge', {
      from,
      to,
    })
  ).data;
  return data;
}

/**
 * @param {AxiosInstance} api
 * @param {string} id
 * @param {string[]} labels
 * @return {Promise<void>}
 */
export async function updateClientLabels(api, id, labels) {
  await api.patch('rml/clientProfile/updateLabels/' + id, { labels: labels });
}

//@@discountLabels vvvvvvvv
export async function updateClientDiscountLabels(api, id, discountLabels) {
  await api.patch('rml/clientProfile/updateDiscountLabels/' + id, {
    discountLabels: discountLabels,
  });
}

// /**
//  * @param {AxiosInstance} api
//  * @param {string} id
//  * @param {string[]} discountLabels
//  * @return {Promise<void>}
//  */
// export async function updateDiscountLabels(api, id, discountLabels) {
//   await api.patch('rml/clientProfile/updateDiscountLabels/' + id, {
//     discountLabels: discountLabels,
//   });
// }

//^^^^^^^^^^^
/**
 * @param {AxiosInstance} api
 * @param id - User ID
 * @param {string}newPw
 */
export async function resetMasterPassword(api, id, newPw) {
  return await api.post(`users/${id}/resetPassword`, { newPw: newPw });
}

/**
 * @param {AxiosInstance} api
 * @param [params]
 * @return {Promise<PaginationResult<IMasterProfile>>}
 */
export async function listMasterProfiles(api, params) {
  const { data } = await api.get('rml/admin/MasterProfiles', {
    params: merge(params, {
      filter: {
        status: ProfileStatus.accepted,
      },
    }),
  });
  return populatedListCtor(data, IProfile);
}
/**
 * @param {AxiosInstance} api
 * @param [params]
 * @return {Promise<PaginationResult<IMasterProfile>>}
 */
export async function listAllClinicProfilesIncludingInactive(api, params, role = 'admin') {
  const requestSettingMap = {
    admin: {
      url: 'rml/admin/ClinicProfiles',
      //params: { filter: { role: { $in: ['bn.cashier', 'bn.master'] } } },
    },
    'rml.clinicStaff': {
      url: 'rml/admin/ClinicProfiles',
      params: {},
    },
  };
  const requestSetting = requestSettingMap[role];
  if (!requestSetting) throw Error(`Role [${role}] Not allow to fetch master profiles`);
  const { data } = await api.get(requestSetting.url, {
    params: merge(requestSetting.params, params),
  });
  return populatedListCtor(data, IProfile);
}

//For master get the data for show on table
/**
 * @param {AxiosInstance} api
 * @param [params]
 * @return {Promise<PaginationResult<IMasterProfile>>}
 */
export async function listAllMasterProfilesIncludingInactiveByMaster(api, params) {
  const { data } = await api.get('rml/master/MasterProfiles', {
    params: merge(params),
  });
  return populatedListCtor(data, IProfile);
}

/**
 * @param {AxiosInstance} api
 * @param [params]
 * @return {Promise<PaginationResult<IMasterProfile>>}
 */
export async function listAllMasterProfilesIncludingInactiveByCashier(api, params) {
  const { data } = await api.get('rml/cashier/Profiles', {
    params: merge({ filter: { role: { $in: ['bn.cashier', 'bn.master'] } } }, params),
  });
  return populatedListCtor(data, IMasterProfile);
}

export class IUser extends GlobalSchemaFields {
  /** @type {String} */
  username;
  /** @type {String} */
  pw;
  /** @type {String} */
  email;
  /**
   * @type {boolean}
   * @readonly
   */
  hasPw;
}

export const ProfileStatus = Object.freeze({
  requested: '0$requested',
  accepted: '1$accepted',
  rejected: '99$rejected',
});

export const relatedAccountsPopulate = {
  path: 'relatedAccounts.profile',
  select: { sid: 1, name: 1, phone: 1, gender: 1, name_chinese: 1 },
};

export class IProfile extends GlobalSchemaFields {
  /** @type {string} */
  role;
  /** @type {string} */
  name;
  /** @type {string} */
  phone;
  /** @type {string} */
  phone2;
  /** @type {string} */
  status;

  constructor(obj) {
    super(obj);
    const clazz =
      {
        //'bn.master': IMasterProfile,
        'rml.client': IClientProfile,
        'bn.master': IMasterProfile,
        'bn.cashier': ICashierProfile,
      }[obj.role] || IProfile;
    if (!(this instanceof clazz)) {
      return new clazz(obj);
    }
    Object.assign(this, obj);
  }
}

export class IMasterProfile extends IProfile {
  /** @type {number} */
  servicingCapacity;
  /** @type {(IAttachment | string)[]} */
  previousHardCopy;
  /** @type {number} */
  bnAutoAssignPriority;
  /** @type {number} */
  bnDisplayOrder;

  /**
   * he has resigned if 'inactive'
   * @type {boolean}
   */
  get isActive() {
    return this.enabled && this.status === ProfileStatus.accepted;
  }

  /** @type {number} */
  get _servicingCapacity() {
    return (
      // handle "inactive" master, so the timetable column shows red
      !this.isActive
        ? 0
        : Number.isFinite(this.servicingCapacity)
        ? this.servicingCapacity
        : Number.POSITIVE_INFINITY
    );
  }

  constructor(obj) {
    super(obj);
    obj.previousHardCopy = populatedArrayCtor(obj.previousHardCopy || [], IAttachment);
    obj.bnEntryDate = parseDate(obj.bnEntryDate);
    Object.assign(this, obj);
  }
}

export class ICashierProfile extends IProfile {
  /** @type {number} */
  servicingCapacity;
  /** @type {(IAttachment | string)[]} */
  previousHardCopy;
  /** @type {number} */
  bnAutoAssignPriority;
  /** @type {number} */
  bnDisplayOrder;

  /**
   * he has resigned if 'inactive'
   * @type {boolean}
   */
  get isActive() {
    return this.enabled && this.status === ProfileStatus.accepted;
  }

  constructor(obj) {
    super(obj);
    obj.previousHardCopy = populatedArrayCtor(obj.previousHardCopy || [], IAttachment);
    obj.bnEntryDate = parseDate(obj.bnEntryDate);
    Object.assign(this, obj);
  }
}

export class IClientProfile extends IProfile {
  /** @type {{heart?: boolean, labels: String[], discountLabels: String[]}} */
  metadata;
  /** @type {Boolean} */
  isBlocked;
  /** @type {String} */
  birthday;
  /** @type {String} */
  gender;
  /** @type {Boolean} */
  oldCustomer;
  /** @type {String} */
  howToKnowBN;
  /** @type {String} */
  otherWayToKnowBN;
  /** @type {Boolean} */
  watchedBN3minIntroduction;
  /** @type {(IAttachment | string)[]} */
  previousHardCopy;
  /** @type {String} */
  phone2;

  constructor(obj) {
    super(obj);
    obj.gender = obj.gender || null;
    obj.metadata = obj.metadata || {};
    obj.metadata.labels = obj.metadata.labels || [];
    obj.metadata.discountLabels = obj.metadata.discountLabels || [];
    obj.metadata.heart = obj.metadata.heart || false;
    obj.previousHardCopy = populatedArrayCtor(obj.previousHardCopy || [], IAttachment);
    Object.assign(this, obj);
  }
}
